import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, message, Modal } from "antd";
import { getTokenUser } from "commons/apis/authClient";
import services from "commons/apis/index";
import { tipsColor } from "constraints/PlanEnum";
import { AuthContext } from "context/AuthContext";
import { isEmpty } from "lodash";
import moment from "moment";
import {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

const { confirm } = Modal;

const convertMoney = (val) => {
  return new Intl.NumberFormat("vi-VN").format(Number(val));
};

const Title = (props) => {
  const { label, title } = props;
  const { t } = useTranslation();

  return (
    <p>
      - {t(`winners.${label}`)}: <span className="font-bold">{title}</span>
    </p>
  );
};

const DetailModal = forwardRef((props, ref) => {
  const { item } = props;
  const { roleLevel, userId } = getTokenUser();
  const { property, attributes, propertyRegisters } = item;

  const { t } = useTranslation();
  const view = attributes.find((attr) => attr.type === "View");
  const { permission } = useContext(AuthContext);

  const [isOpen, setIsOpen] = useState(false);

  const onClose = () => {
    setIsOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: onClose,
  }));

  const renderStatus = useCallback(
    (statusName) => {
      if (roleLevel !== 4 && statusName === "Complete") {
        return "Deposited";
      }
      return statusName;
    },
    [roleLevel]
  );

  const renderInfo = () => (
    <>
      {property?.bed && <Title label="bedroom" title={`${property.bed}`} />}
      {property?.bath && <Title label="bathroom" title={`${property.bath}`} />}
      {view?.attribute && (
        <Title label="view" title={t(`winners.${view?.attribute}`)} />
      )}
      <Title label="squareGFA" title={`${property.squareNFA?.toFixed(2)} m²`} />
      <Title label="squareNFA" title={`${property.squareGFA?.toFixed(2)} m²`} />
      {permission?.includes("Pages.Properties.ViewPrice") && (
        <>
          <Title
            label="priceIncludeVat"
            title={`${convertMoney(property.priceIncludeVat)} VND`}
          />
          <Title
            label="priceExculdeVat"
            title={`${convertMoney(property.priceExcludeVat)} VND`}
          />
        </>
      )}

      <Title
        label="status"
        title={t(`winners.${renderStatus(property.status)}`)}
      />
    </>
  );

  const renderRegister = () => {
    return (
      !isEmpty(propertyRegisters) && (
        <div className="register-block">
          <p className="register-label">{t("winners.registerList")}</p>
          <div className="register-list">
            {propertyRegisters.map((register, index) => {
              const startTime = moment(register.expiryDateTime)
                .subtract(register.limitedTimeInMinutes, "minutes")
                .format("HH:mm:ss");
              const endTime = moment(register.expiryDateTime).format(
                "HH:mm:ss"
              );

              return (
                <div key={index} className="register-item">
                  <p className="mr-1">{register.agentName}</p>
                  <p>
                    ({startTime} - {endTime})
                  </p>
                  <div className="register-status">
                    <p
                      className={
                        ["InProgress", "Completed", "Cancelled"].includes(
                          register.status
                        )
                          ? "text-white"
                          : "text-black"
                      }
                      style={{
                        backgroundColor:
                          tipsColor[renderStatus(register.status)],
                      }}
                    >
                      {t(`status.${renderStatus(register.status)}`)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )
    );
  };

  const showConfirm = () => {
    let modal;
    modal = confirm({
      centered: true,
      className: "register-confirm",
      title: t("winners.register_confirm"),
      icon: <ExclamationCircleOutlined />,
      cancelText: t("winners.cancel"),
      okText: t("winners.confirm"),
      okButtonProps: { loading: false },
      async onOk() {
        return new Promise(async (resolve, reject) => {
          modal.update({ okButtonProps: { loading: true } });
          try {
            const response = await services.postRegister({
              propertyId: property.id,
            });
            if (response.success) {
              message.success(
                t(`winners.register_success`, {
                  propertyNo: property.propertyNo,
                }),
                3
              );
              resolve();
            } else {
              const errorMsg = response?.error?.message;
              message.error(
                errorMsg ??
                  t("winners.register_fail", {
                    propertyNo: property.propertyNo,
                  }),
                3
              );
              onClose();
              modal.destroy();
              resolve();
            }
          } catch (error) {
            throw error;
          } finally {
            modal.update({ okButtonProps: { loading: false } });
          }
        });
      },
    });
  };

  return (
    <Modal
      title={`${property.propertyNo} - ${
        !isEmpty(item.agentName) ? item.agentName : t("winners.cartPublic")
      }`}
      className="cell-modal"
      footer={null}
      open={isOpen}
      onCancel={onClose}
      centered
      destroyOnClose
    >
      <div className="cell-content">
        {renderInfo()}
        {renderRegister()}
      </div>
      <div className="cell-button">
        <Button onClick={onClose}>{t("winners.cancel")}</Button>
        {roleLevel !== 4 && (
          <>
            {item.property.status === "Available" && !item.agentName && (
              <Button
                type="primary"
                onClick={showConfirm}
                disabled={propertyRegisters.some(
                  (register) => register.userId === userId
                )}
              >
                {t(
                  propertyRegisters.some(
                    (register) => register.userId === userId
                  )
                    ? "winners.registered"
                    : "winners.register"
                )}
              </Button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
});

DetailModal.displayName = "DetailModal";

export default DetailModal;
